import React, { useState } from 'react'
import { Modal, Input, Form } from 'semantic-ui-react'
import styled from 'styled-components'
import { AppPrimaryButton, AppButton } from 'elements'
import { FlexRowBetween, TitleText } from 'components'
import { X, Trash } from 'phosphor-react'
import { useNotification } from 'hooks/toast'
import { InspectionService } from 'utility'

const StyledModal = styled(Modal)`
    width: 75% !important;
    max-width: 75% !important;
    max-height: 90vh !important;
    margin: 5vh auto !important;
    display: flex !important;
    flex-direction: column;

    .content {
        max-height: calc(90vh - 170px);
        overflow-y: auto;
    }

    @media (max-width: 1366px) {
        width: 82% !important;
        max-width: 82% !important;
    }

    @media (max-width: 992px) {
        width: 92% !important;
        max-width: 92% !important;
    }
`

const ListContainer = styled.div`
    max-height: 220px;
    overflow-y: auto;
    overflow-x: auto;
    margin-top: 16px;
    padding: 8px 12px 8px 8px;
    border: 1px solid #e8e9e9;
    border-radius: 4px;
    background: #ffffff;
`

const Row = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px;
    margin-bottom: 8px;
    background: #f8f9fa;
    border-radius: 4px;
    min-width: 0;

    &:last-child {
        margin-bottom: 0;
    }
`

const RowText = styled.span`
    flex: 1;
    min-width: 0;
    font-size: 14px;
    overflow-wrap: anywhere;
`

const DeleteIcon = styled(Trash)`
    flex-shrink: 0;
    cursor: pointer;
    color: #dc3545;
    &:hover {
        color: #c82333;
    }
`

const ActionsContainer = styled(Modal.Actions)`
    display: flex !important;
    flex-direction: row;
    justify-content: flex-end;
    gap: 10px;

    > button {
        min-width: 140px;
    }

    @media (max-width: 640px) {
        flex-direction: column;

        > button {
            width: 100%;
        }
    }
`

export const SendReportEmailModal = ({
    open,
    onClose,
    selectedReports,
    onRemoveReport,
}) => {
    const [emailInput, setEmailInput] = useState('')
    const [emails, setEmails] = useState([])
    const [sending, setSending] = useState(false)
    const { successToast, errorToast } = useNotification()

    const validateEmail = email => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    const handleAddEmail = () => {
        const trimmed = emailInput.trim()
        if (!trimmed) {
            errorToast('Please enter an email address')
            return
        }
        if (!validateEmail(trimmed)) {
            errorToast('Please enter a valid email address')
            return
        }
        if (emails.includes(trimmed)) {
            errorToast('This email address is already added')
            return
        }
        setEmails([...emails, trimmed])
        setEmailInput('')
    }

    const handleRemoveEmail = email => {
        setEmails(emails.filter(e => e !== email))
    }

    const handleSend = async () => {
        if (!selectedReports || selectedReports.length === 0) {
            errorToast('Please select at least one report')
            return
        }
        if (!emails.length) {
            errorToast('Please add at least one email address')
            return
        }

        setSending(true)
        try {
            const uniqueReports = selectedReports.filter(
                (r, idx, arr) =>
                    idx === arr.findIndex(x => x.TaskId === r.TaskId),
            )

            // Use TaskId from CustomDashboard response as AuditIds payload
            const auditIds = uniqueReports
                .map(r => r.TaskId)
                .filter(id => !!id)

            await InspectionService.sendMultiReport({
                AuditIds: auditIds,
                Emails: emails,
            })

            successToast(
                `Email sent successfully for ${uniqueReports.length} report(s)`,
            )
            onClose()
        } catch (error) {
            console.error('Error sending report emails:', error)
            errorToast('Failed to send email')
        } finally {
            setSending(false)
        }
    }

    const handleClose = () => {
        setEmailInput('')
        setEmails([])
        onClose()
    }

    return (
        <StyledModal open={open} onClose={handleClose} className="model">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>Send Email</TitleText>
                    <X className="pointer" size={32} onClick={handleClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Field>
                        <label>Email Address</label>
                        <Input
                            placeholder="Enter email address"
                            value={emailInput}
                            onChange={e => setEmailInput(e.target.value)}
                            action={{
                                content: 'Add',
                                onClick: handleAddEmail,
                                disabled: sending,
                            }}
                        />
                    </Form.Field>
                </Form>

                {emails.length > 0 && (
                    <>
                        <label style={{ marginTop: 16, display: 'block' }}>
                            Added Emails
                        </label>
                        <ListContainer>
                            {emails.map(email => (
                                <Row key={email}>
                                    <RowText>{email}</RowText>
                                    <DeleteIcon
                                        size={18}
                                        onClick={() => handleRemoveEmail(email)}
                                    />
                                </Row>
                            ))}
                        </ListContainer>
                    </>
                )}

                {selectedReports && selectedReports.length > 0 && (
                    <>
                        <label style={{ marginTop: 16, display: 'block' }}>
                            Selected Reports
                        </label>
                        <ListContainer>
                            {selectedReports.map(report => (
                                <Row key={report.TaskId}>
                                    <RowText>
                                        {report?.TemplateData?.AuditName ||
                                            report?.TemplateName ||
                                            ''}
                                    </RowText>
                                    <DeleteIcon
                                        size={18}
                                        onClick={() =>
                                            onRemoveReport &&
                                            onRemoveReport(report)
                                        }
                                    />
                                </Row>
                            ))}
                        </ListContainer>
                    </>
                )}
            </Modal.Content>
            <ActionsContainer>
                <AppButton
                    type="button"
                    onClick={handleClose}
                    disabled={sending}
                >
                    Cancel
                </AppButton>
                <AppPrimaryButton
                    type="button"
                    onClick={handleSend}
                    loading={sending}
                    disabled={sending}
                >
                    Send Email
                </AppPrimaryButton>
            </ActionsContainer>
        </StyledModal>
    )
}
